<script setup>
/**
 * RED-262: shared "Sarnasuse kaalud" control panel.
 *
 * Two-way binds a UI-scale config object (embeddings + geographic distance on a ×scale
 * slider, price + enrichment raw). The same shape the backend expects via
 * EvaluationConfigDto::fromRequest. Used by the model explorer.
 *
 * Slider min/max/step come from the `bounds` prop — the single source of truth
 * (App\Services\Valuation\SimilarityWeightBounds) shared with request validation and the
 * calibration optimiser's search space, so the controls always cover exactly the usable
 * range and can never drift from it.
 */
const config = defineModel({ type: Object, required: true })

const props = defineProps({
  // Per-knob { min, max, step, scale } in UI scale. Defaults mirror the canonical source
  // (App\Services\Valuation\SimilarityWeightBounds → redata-api OptimizeBounds) so the
  // component still renders sanely if a caller forgets to pass the `bounds` prop.
  bounds: {
    type: Object,
    default: () => ({
      embed_weights: { min: 0, max: 200, step: 1, scale: 100 },
      zdist_weight: { min: 0, max: 600, step: 1, scale: 100 },
      price_diff_weight: { min: 0, max: 3, step: 0.1, scale: 1 },
      rikastatus_weight: { min: 0, max: 5, step: 0.1, scale: 1 },
    }),
  },
  // Concrete served CatBoost ids to offer alongside the generic aliases.
  // 'catboost' resolves API-side to the newest default-eligible CatBoost;
  // a concrete id pins that exact bundle for the run.
  catboostModels: { type: Array, default: () => [] },
  // RED-356: API keys of the controls the admin moved off the live value (model explorer), and
  // the live values themselves, keyed the same way (embed_weights, zdist_weight, …, UI scale).
  changed: { type: Array, default: () => [] },
  live: { type: Object, default: null },
})

const fmt = (value, key) => {
  const { scale } = props.bounds[key]
  return (Number(value) / scale).toFixed(scale > 1 ? 2 : 1)
}

// RED-356: a changed control gets an orange label and dot; its title names the live value.
const isChanged = key => props.changed.includes(key)
const embedChanged = i =>
  isChanged('embed_weights') &&
  (props.live?.embed_weights == null ||
    Math.abs(Number(config.value.embedWeights[i]) - Number(props.live.embed_weights[i])) >= 1e-6)
const labelClass = changed => (changed ? 'text-orange-600 font-medium' : 'text-gray-600')
const liveTitle = (changed, text) => (changed ? `Muudetud — live: ${text}` : undefined)
const liveWeight = key => (props.live?.[key] == null ? '–' : fmt(props.live[key], key))

const embedLabels = ['Mudeli tunnus 1', 'Mudeli tunnus 2', 'Pindala', 'Tubade arv']

const nSimilarOptions = [
  { label: '3 tehingut', value: 3 },
  { label: '5 tehingut', value: 5 },
  { label: '7 tehingut', value: 7 },
]

const recentMonthsOptions = [
  { label: '6 kuud', value: 6 },
  { label: '12 kuud', value: 12 },
  { label: '18 kuud', value: 18 },
  { label: '24 kuud', value: 24 },
]

import { computed } from 'vue'

const priceModelOptions = computed(() => [
  { label: 'CatBoost (uusim)', value: 'catboost' },
  { label: 'EBM', value: 'ebm' },
  ...props.catboostModels.map((id) => ({ label: id, value: id })),
])
</script>

<template>
  <div class="space-y-3">
    <!-- Embedding weights -->
    <div>
      <h4 class="text-xs font-semibold uppercase tracking-wide text-gray-500 mb-1.5">Mudeli tunnused</h4>
      <div class="space-y-2">
        <div v-for="(label, i) in embedLabels" :key="i" class="flex items-center gap-2">
          <span
            class="w-28 shrink-0 text-xs truncate"
            :class="labelClass(embedChanged(i))"
            :title="liveTitle(embedChanged(i), live?.embed_weights ? fmt(live.embed_weights[i], 'embed_weights') : '–')"
          >
            <i v-if="embedChanged(i)" class="pi pi-circle-fill text-[6px] text-orange-500 mr-1 align-middle" />{{ label }}
          </span>
          <Slider
            v-model="config.embedWeights[i]"
            :min="bounds.embed_weights.min"
            :max="bounds.embed_weights.max"
            :step="bounds.embed_weights.step"
            class="flex-1"
          />
          <span class="w-10 shrink-0 text-right font-mono text-xs text-gray-500">
            {{ fmt(config.embedWeights[i], 'embed_weights') }}
          </span>
        </div>
      </div>
    </div>

    <!-- Location + extra weights -->
    <div>
      <h4 class="text-xs font-semibold uppercase tracking-wide text-gray-500 mb-1.5">Asukoht ja lisakaalud</h4>
      <div class="space-y-2">
        <div class="flex items-center gap-2">
          <span
            class="w-28 shrink-0 text-xs truncate"
            :class="labelClass(isChanged('zdist_weight'))"
            :title="liveTitle(isChanged('zdist_weight'), liveWeight('zdist_weight'))"
          >
            <i v-if="isChanged('zdist_weight')" class="pi pi-circle-fill text-[6px] text-orange-500 mr-1 align-middle" />Geograafiline kaugus
          </span>
          <Slider
            v-model="config.zdistWeight"
            :min="bounds.zdist_weight.min"
            :max="bounds.zdist_weight.max"
            :step="bounds.zdist_weight.step"
            class="flex-1"
          />
          <span class="w-10 shrink-0 text-right font-mono text-xs text-gray-500">
            {{ fmt(config.zdistWeight, 'zdist_weight') }}
          </span>
        </div>
        <div class="flex items-center gap-2">
          <span
            class="w-28 shrink-0 text-xs truncate"
            :class="labelClass(isChanged('price_diff_weight'))"
            :title="liveTitle(isChanged('price_diff_weight'), liveWeight('price_diff_weight'))"
          >
            <i v-if="isChanged('price_diff_weight')" class="pi pi-circle-fill text-[6px] text-orange-500 mr-1 align-middle" />Hinna sarnasus
          </span>
          <Slider
            v-model="config.priceDiffWeight"
            :min="bounds.price_diff_weight.min"
            :max="bounds.price_diff_weight.max"
            :step="bounds.price_diff_weight.step"
            class="flex-1"
          />
          <span class="w-10 shrink-0 text-right font-mono text-xs text-gray-500">
            {{ fmt(config.priceDiffWeight, 'price_diff_weight') }}
          </span>
        </div>
        <div class="flex items-center gap-2">
          <span
            class="w-28 shrink-0 text-xs truncate"
            :class="labelClass(isChanged('rikastatus_weight'))"
            :title="liveTitle(isChanged('rikastatus_weight'), liveWeight('rikastatus_weight'))"
          >
            <i v-if="isChanged('rikastatus_weight')" class="pi pi-circle-fill text-[6px] text-orange-500 mr-1 align-middle" />Rikastatud andmed
          </span>
          <Slider
            v-model="config.rikastatusWeight"
            :min="bounds.rikastatus_weight.min"
            :max="bounds.rikastatus_weight.max"
            :step="bounds.rikastatus_weight.step"
            class="flex-1"
          />
          <span class="w-10 shrink-0 text-right font-mono text-xs text-gray-500">
            {{ fmt(config.rikastatusWeight, 'rikastatus_weight') }}
          </span>
        </div>
      </div>
    </div>

    <!-- Selection options -->
    <div>
      <h4 class="text-xs font-semibold uppercase tracking-wide text-gray-500 mb-1.5">Valikud</h4>
      <div class="space-y-2">
        <div class="flex items-center gap-2">
          <span class="w-28 shrink-0 text-xs text-gray-600">Sarnaste arv</span>
          <Select
            v-model="config.nSimilar"
            :options="nSimilarOptions"
            option-label="label"
            option-value="value"
            size="small"
            class="flex-1 min-w-0"
          />
        </div>
        <div class="flex items-center gap-2">
          <span
            class="w-28 shrink-0 text-xs"
            :class="labelClass(isChanged('recent_months'))"
            :title="liveTitle(isChanged('recent_months'), `${live?.recent_months ?? '–'} kuud`)"
          >
            <i v-if="isChanged('recent_months')" class="pi pi-circle-fill text-[6px] text-orange-500 mr-1 align-middle" />Ajaperiood
          </span>
          <Select
            v-model="config.recentMonths"
            :options="recentMonthsOptions"
            option-label="label"
            option-value="value"
            size="small"
            class="flex-1 min-w-0"
          />
        </div>
        <div class="flex items-center gap-2">
          <span
            class="w-28 shrink-0 text-xs truncate"
            :class="labelClass(isChanged('similarity_price_model'))"
            :title="liveTitle(isChanged('similarity_price_model'), live?.similarity_price_model ?? '–')"
          >
            <i v-if="isChanged('similarity_price_model')" class="pi pi-circle-fill text-[6px] text-orange-500 mr-1 align-middle" />Sarnasuse mudel
          </span>
          <Select
            v-model="config.similarityPriceModel"
            :options="priceModelOptions"
            option-label="label"
            option-value="value"
            size="small"
            class="flex-1 min-w-0"
          />
        </div>
      </div>
    </div>
  </div>
</template>
